import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as DeviceActions from '../../../../../actions/device'
import { ACL, colors } from '../../../../../constants'
import { EstateUtils } from '../../../../../utils'
import Confirm from '../../../../Confirm'
import Headings from '../../../../Headings'
import Input from '../../../../Input'
import Padding, { Sizes as PaddingSizes } from '../../../../Padding'
import { Icon } from '../Store.styles'
import { StyledLink } from '../ActionLink/ActionLink.style'

interface DeviceDeletionProps {
    deviceId: string
    reloadStore: () => void
}

const DeletionReasons = [
    { name: 'Device was removed', value: 'DEVICE_REMOVED' },
    { name: 'Device is faulty', value: 'DEVICE_FAULTY' },
    { name: 'Returned to Volume', value: 'RETURNED_VOLUME' },
    { name: 'Returned under warranty', value: 'RETURNED_WARRANTY' },
    { name: 'End of life', value: 'EOL' },
    { name: 'Lost or Stolen', value: 'LOST_STOLEN' },
    { name: 'Other', value: 'OTHER' },
] as const

type DeletionReasonsValue = typeof DeletionReasons[number]['value']

function DeviceDeletion({ deviceId, reloadStore }: DeviceDeletionProps) {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const [selectedReason, setSelectedReason] = React.useState<DeletionReasonsValue | null>(null)
    const [otherReason, setOtherReason] = React.useState<string>()
    const [error, setError] = React.useState<string | null>(null)
    const [isConfirmationOpen, setIsConfirmationOpen] = React.useState<boolean>(false)
    const [isWorking, setIsWorking] = React.useState<boolean>(false)
    const userCanDeleteDevice = useSelector(
        (state) => EstateUtils.hasPermission(state, { acls: [ACL.DEVICE_DELETE] }),
    )
    const showOtherReasonInput = selectedReason === 'OTHER'

    React.useEffect(() => {
        if (isConfirmationOpen) {
            setSelectedReason(null)
        }
    }, [isConfirmationOpen])

    if (!userCanDeleteDevice) {
        return null
    }

    const onChangeReason = ({ target: { value }}: { target: { value: DeletionReasonsValue }}) => {
        setError(null)
        setSelectedReason(value)
    }

    const onChangeOtherReason = ({ target: { value }}: { target: { value: string }}) => {
        setOtherReason(value)
    }

    const onClickAccept = async () => {
        if (!selectedReason) {
            setError('You need to select a reason')
            return
        }

        let reason: string = selectedReason

        if (selectedReason === 'OTHER' && otherReason) {
            reason = otherReason
        }

        setIsWorking(true)

        const response = await dispatch(DeviceActions.deleteDevice(deviceId, { reason }))

        setIsWorking(true)

        if (response.type === DeviceActions.Types.DELETE_DEVICE_SUCCESS) {
            setIsConfirmationOpen(false)
            reloadStore()
        }
    }

    const displayReasons = DeletionReasons.map(reason => {
        return {
            ...reason,
            name: t(`device-deletion-${reason.value.toLowerCase()}`),
        }
    })

    return (
        <>
            <StyledLink onClick={() => setIsConfirmationOpen(true)}>
                <Icon icon="trash-alt" title={t('Delete device')} color={colors.red} />
            </StyledLink>
            <Confirm
                isOpen={isConfirmationOpen}
                header={t('device-deletion-header', { device: deviceId })}
                content={(
                    <Padding right bottom>
                        <Padding bottom>{t('device-deletion-reason')}</Padding>
                        <Input
                            type="select"
                            onChange={onChangeReason}
                            size="lg"
                            value={selectedReason}
                            values={displayReasons}
                            error={error}
                            disabled={false}
                            borderColor={error && colors.red}
                        />
                        <Padding top size={PaddingSizes.xs}>
                            <Headings.H4 color={colors.red}>{error}</Headings.H4>
                        </Padding>
                        {showOtherReasonInput && (
                            <Input
                                type="textarea"
                                onChange={onChangeOtherReason}
                                size="lg"
                                value={otherReason}
                            />
                        )}
                    </Padding>
                )}
                onClickCancel={() => setIsConfirmationOpen(false)}
                onClickAccept={onClickAccept}
                isWorking={isWorking}
            />
        </>
    )
}

export default DeviceDeletion
